import { useMemo, useEffect, useRef } from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'

// Height constants for estimation (calibrated from measured DOM heights).
const GROUP_HEADER_HEIGHT = 40
const ROW_BASE_HEIGHT = 140
const EXTRA_LINE_ITEM_HEIGHT = 34

/**
 * Estimate the pixel height of a flat-list entry based on its data.
 * Used by the virtualiser for accurate initial layout before measurement.
 */
function estimateEntryHeight(entry) {
  // Group: header + sum of row heights
  const rowsHeight = entry.items.reduce((sum, item) => {
    const extraLines = Math.max(0, item.items.length - 1)
    return sum + ROW_BASE_HEIGHT + extraLines * EXTRA_LINE_ITEM_HEIGHT
  }, 0)

  return GROUP_HEADER_HEIGHT + rowsHeight
}

/**
 * useTimelineVirtualiser
 *
 * Virtualises the timeline at date-group level using @tanstack/react-virtual.
 * Produces a flat array of past groups and manages infinite scroll for loading
 * more history.
 *
 * @param {Object} params
 * @param {Array<{ date: string, items: Array }>} params.pastGroups
 * @param {import('react').RefObject<HTMLDivElement>} params.scrollContainerRef
 * @param {boolean} params.hasMorePast
 * @param {boolean} params.loadingPast
 * @param {Function} params.onLoadMorePast
 * @param {boolean} params.hasInitiallyScrolled
 * @param {boolean} params.isSettling
 *
 * @returns {{ virtualizer: object, flatList: Array, currentDateLabel: string|null, totalSize: number, virtualItems: Array }}
 */
export function useTimelineVirtualiser({
  pastGroups,
  scrollContainerRef,
  hasMorePast,
  loadingPast,
  onLoadMorePast,
  hasInitiallyScrolled,
  isSettling,
}) {
  const OVERSCAN = 5

  // Build flat list: past groups only
  const flatList = useMemo(() => {
    return pastGroups.map(g => ({
      type: 'past-group',
      date: g.date,
      items: g.items,
    }))
  }, [pastGroups])

  // Virtualiser instance
  const virtualizer = useVirtualizer({
    count: flatList.length,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: (index) => estimateEntryHeight(flatList[index]),
    overscan: OVERSCAN,
  })

  const virtualItems = virtualizer.getVirtualItems()
  const totalSize = virtualizer.getTotalSize()

  // --- Infinite scroll via virtual range detection ---
  const loadingPastRef = useRef(loadingPast)
  loadingPastRef.current = loadingPast

  useEffect(() => {
    if (!hasInitiallyScrolled) return
    if (isSettling) return
    if (virtualItems.length === 0) return

    const lastIndex = virtualItems[virtualItems.length - 1].index

    // Near the bottom → load more past
    if (
      lastIndex >= flatList.length - 1 - OVERSCAN &&
      hasMorePast &&
      !loadingPastRef.current &&
      onLoadMorePast
    ) {
      onLoadMorePast()
    }
  }, [virtualItems, hasInitiallyScrolled, isSettling, hasMorePast, flatList.length, onLoadMorePast])

  // --- Current group data + push offset for iOS-style sticky header ---
  const STICKY_HEADER_HEIGHT = 32
  const scrollOffset = virtualizer.scrollOffset ?? 0

  const stickyHeaderData = useMemo(() => {
    let currentDate = null
    let currentItems = []
    let pushOffset = 0
    let foundCurrent = false

    for (const vItem of virtualItems) {
      const entry = flatList[vItem.index]
      if (!entry) continue

      const relativeTop = vItem.start - scrollOffset

      if (relativeTop <= 0) {
        // This group's top is at or above the scroll position — it's the current group
        currentDate = entry.date
        currentItems = entry.items
        foundCurrent = true
      } else if (foundCurrent) {
        // First group below the scroll position — check for push effect
        if (relativeTop < STICKY_HEADER_HEIGHT) {
          pushOffset = relativeTop - STICKY_HEADER_HEIGHT
        }
        break
      }
    }

    // Fallback: use first visible entry
    if (!currentDate && virtualItems.length > 0) {
      const entry = flatList[virtualItems[0].index]
      if (entry) {
        currentDate = entry.date
        currentItems = entry.items
      }
    }

    // At the top of the list: slide header out to avoid duplicating
    // the first group header which is already visible
    if (scrollOffset < STICKY_HEADER_HEIGHT && pushOffset === 0) {
      pushOffset = scrollOffset - STICKY_HEADER_HEIGHT
    }

    return { currentDate, currentItems, pushOffset }
  }, [virtualItems, flatList, scrollOffset, STICKY_HEADER_HEIGHT])

  return {
    virtualizer,
    flatList,
    stickyHeaderData,
    totalSize,
    virtualItems,
  }
}
